import dynamic from 'next/dynamic'
import { AchieveLink } from 'components/AchieveLink'
import { mainProductConfig } from 'constants/mainProductConfig'
import { useEffect, useState } from 'react'
import { Typography } from 'components/Contentful'
import useMobileNavState from 'hooks/useMobileNavState'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import MenuIcon from 'components/Svgs/Menu.js'
import Close from 'components/Svgs/Close.js'
import useIsSsr from 'hooks/useIsSsr'
import classNames from 'classnames'
import BillsHeaderNav from './BillsHeaderNav'
import useScrollDirection from 'hooks/useScrollDirection'
import BillsHeaderSticky from './BillsHeaderSticky'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import useScrollBelow from 'hooks/useScrollBelow'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { cleanDomain } from '@/utils/cleanDomain'

const WebsiteBillsLogoSVG = dynamic(() => import('components/Svgs/WebsiteBillsLogoSVG.js'))
const WebsiteBillsLogoWhiteSVG = dynamic(() =>
  import('components/Svgs/WebsiteBillsLogoWhiteSVG.js')
)

const STICKY_HEADER_BLACKLIST = ['/']

const CurrentSvg = ({ ...props }) => {
  return <WebsiteBillsLogoSVG title="Bills Logo" alt="Bills Logo" {...props} />
}

export default function BillsHeader({
  siteEnv = 'website-bills',
  logoEvent,
  linkUrl,
  linkText,
  mainNavigation,
  footerSticky,
  dynamicStickyNav,
}) {
  const [mobileNavOpen, setMobileNavOpen] = useMobileNavState()
  const { fireAndForget } = useTriggerEvents()
  const isSsr = useIsSsr()
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState(null)
  const { isScrollingDown, isScrollingUp } = useScrollDirection()
  // Show sticky section only after scrolling past 600px of the page
  const isBelowStickyThreshold = useScrollBelow(120)
  const [, setLayoutMenuCondensed] = useLayoutMenuCondensed()
  const { dynamicPathname } = useAppRouter()
  const [domain, setDomain] = useState('')

  useEffect(() => {
    if (window) {
      setDomain(cleanDomain(window.location.href))
    }
  }, [])

  useEffect(() => {
    // Toggle header condensation based on scroll position
    setLayoutMenuCondensed(isBelowStickyThreshold)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBelowStickyThreshold])

  const handleMainItemClick = (item) => {
    setActiveSubMenuIndex(item)
  }

  const handleBackToMain = () => {
    setActiveSubMenuIndex(null)
  }

  const handleCloseClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !isSsr && window.scrollTo({ top: 0, behavior: 'instant' })
    setMobileNavOpen(false)
  }

  const handleOpenClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    setMobileNavOpen(true)
    setActiveSubMenuIndex(null)
  }

  const showStickyHeader =
    !STICKY_HEADER_BLACKLIST.includes(dynamicPathname) &&
    (footerSticky?.card?.title || dynamicStickyNav?.content?.fields?.cardTitleText)

  return (
    <div className="flex flex-col">
      <div
        className={classNames(
          'fixed top-0 left-0 w-full bg-white z-20 transition-transform duration-300 ease-in-out transform',
          {
            'translate-y-[-50px]': isScrollingDown && !mobileNavOpen,
            'translate-y-0': (isScrollingUp || !isScrollingDown) && !mobileNavOpen,
          }
        )}
      >
        <div className="p-2 border-2 border-solid border-gray-200 border-l-0 border-r-0 border-t-0">
          <header
            className={classNames(
              'flex flex-row items-center gap-4 transition-transform duration-300 ease-in-out transform max-w-[1280px] mx-auto z-20',
              {
                'translate-x-80 lg:translate-x-0': mobileNavOpen,
              }
            )}
          >
            <div className="flex flex-col justify-center items-center">
              <button
                onClick={(e) => {
                  handleOpenClick(e, {
                    nav_link_section: 'Toggle menu',
                    click_type: 'Button Click',
                    click_id: 'open button',
                  })
                }}
                className="border-none bg-transparent cursor-pointer flex gap-2 items-center"
                data-testid="mobile-nav-open-button"
                aria-label="Main navigation menu button"
              >
                <MenuIcon /> <Typography className="bodyMd text-slate-600" content="Menu" />
              </button>
            </div>
            <div className={'w-full flex justify-center'}>
              <AchieveLink
                track={{
                  ...{
                    list_name: 'Header Logo',
                    click_id: `${mainProductConfig[siteEnv || 'achieve']?.['HeaderLogo']}`,
                    click_text: `${
                      mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                    } | Header logo`,
                    nav_link_section: 'Header logo',
                    track_event: 'global_header',
                    event_action: 'link_click',
                    event_type: 'link_click',
                  },
                  ...logoEvent,
                }}
                href={domain}
                withNextLink
                className={''}
                data-testid={`${String(
                  mainProductConfig[siteEnv || 'achieve']?.['MainName']
                )?.toLowerCase()}-header-logo`}
              >
                <CurrentSvg siteEnv={siteEnv} />
              </AchieveLink>
            </div>
          </header>
          <nav
            className={classNames(
              'min-w-[20rem] w-[20rem] bg-white h-screen absolute top-0 left-0 overflow-hidden z-10 transition-transform duration-300 ease-in-out transform bg-[#104c81]! z-20',
              {
                '-translate-x-80': !mobileNavOpen,
                'translate-x-0': mobileNavOpen,
              }
            )}
          >
            <div className="flex flex-row justify-end p-4 stroke-white fill-white">
              <button
                onClick={(e) => {
                  handleCloseClick(e, {
                    nav_link_section: 'Toggle menu',
                    click_type: 'Button Click',
                    click_id: 'close button',
                  })
                }}
                className="border-none bg-transparent cursor-pointer"
                role="button"
                data-testid="mobile-nav-close-button"
                aria-label="Main navigation menu close button"
              >
                <Close title="Close button" width={18} height={18} />
              </button>
            </div>
            <div className="flex flex-row p-3">
              <WebsiteBillsLogoWhiteSVG width={110} />
            </div>
            <BillsHeaderNav
              mainNavigation={mainNavigation}
              activeSubMenuIndex={activeSubMenuIndex}
              handleMainItemClick={handleMainItemClick}
              handleBackToMain={handleBackToMain}
              handleCloseMenu={() => setMobileNavOpen(false)}
              siteEnv={siteEnv}
            />
          </nav>
          {mobileNavOpen && (
            <div className="bg-black/25 w-screen h-screen absolute top-0 left-0 z-10"></div>
          )}
        </div>

        {showStickyHeader && (
          <BillsHeaderSticky
            siteEnv={siteEnv}
            CurrentSvg={CurrentSvg}
            tp_title={
              dynamicStickyNav
                ? dynamicStickyNav?.content?.fields?.cardTitleText
                : footerSticky?.card?.title
            }
            tp_image={
              dynamicStickyNav
                ? dynamicStickyNav?.content?.fields?.cardImage
                : footerSticky?.card?.image
            }
            tp_text={dynamicStickyNav ? null : footerSticky?.card?.subtitle}
            linkUrl={dynamicStickyNav ? dynamicStickyNav?.link?.fields?.linkHref : linkUrl}
            linkText={
              dynamicStickyNav
                ? dynamicStickyNav?.link?.fields?.linkText?.content[0]?.content[0]?.value
                : linkText
            }
            subLinkText={
              dynamicStickyNav ? dynamicStickyNav?.linkSubtext : 'From Freedom Debt Relief'
            }
          />
        )}
      </div>
    </div>
  )
}
